import { RiskRegisterDisplayRowKind } from "src/app/enums/riskRegisterDisplayRowKind.enum";
import { TableExtendedRiskRegister } from "src/app/models/risk-assessment.model";
import {
    RiskRegisterDisplayRow,
    RiskRegisterGroupedEntry,
    RiskRegisterGroupedRowResponse,
    RiskRegisterGroupHeaderRow,
    RiskRegisterGroupResponse,
    RiskRegisterSegDisplayRow,
} from "src/app/models/risk-register-grouping.model";

const FALLBACK_GROUP_KEY = "none";
const FLAT_GROUP_KEY = "";
const PATH_SEPARATOR = " > ";

export function groupKeyOf(orgLevelId: string | null): string {
    return orgLevelId ?? FALLBACK_GROUP_KEY;
}

function foldName(name: string | null): string {
    return (name ?? "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

function fullPath(group: RiskRegisterGroupResponse): string {
    return [...group.parentPath, group.name ?? ""].join(PATH_SEPARATOR);
}

function groupLabelOf(name: string, segCount: number): string {
    return `${name} (${segCount})`;
}

export function buildGroupLabels(
    groups: RiskRegisterGroupResponse[],
    fallbackLabel: string,
): Map<string, Pick<RiskRegisterGroupHeaderRow, "name" | "label" | "title" | "segCount">> {
    const nameCounts = new Map<string, number>();
    groups.forEach((group) => {
        const folded = foldName(group.name);
        nameCounts.set(folded, (nameCounts.get(folded) ?? 0) + 1);
    });
    return new Map(
        groups.map((group) => {
            if (group.orgLevelId == null) {
                return [groupKeyOf(null), { name: fallbackLabel, label: groupLabelOf(fallbackLabel, group.segCount), title: fallbackLabel, segCount: group.segCount }];
            }
            const path = fullPath(group);
            const shown = (nameCounts.get(foldName(group.name)) ?? 0) > 1 ? path : (group.name ?? "");
            return [groupKeyOf(group.orgLevelId), { name: shown, label: groupLabelOf(shown, group.segCount), title: path, segCount: group.segCount }];
        }),
    );
}

function reuseOrCreate<T extends RiskRegisterDisplayRow>(previous: Map<string, RiskRegisterDisplayRow>, next: T, isSame: (old: T) => boolean): T {
    const old = previous.get(next.key) as T | undefined;
    return old && old.kind === next.kind && isSame(old) ? old : next;
}

function segRow(
    previous: Map<string, RiskRegisterDisplayRow>,
    seg: TableExtendedRiskRegister,
    groupKey: string,
    numberOfWorkers: number | null,
    numberOfContractors: number | null,
    even: boolean,
): RiskRegisterSegDisplayRow {
    const next: RiskRegisterSegDisplayRow = {
        kind: RiskRegisterDisplayRowKind.Seg,
        key: `${groupKey}|${seg.segId}`,
        groupKey,
        seg,
        numberOfWorkers,
        numberOfContractors,
        even,
    };
    return reuseOrCreate(
        previous,
        next,
        (old) =>
            old.seg === seg &&
            old.numberOfWorkers === numberOfWorkers &&
            old.numberOfContractors === numberOfContractors &&
            old.even === even,
    );
}

export function buildFlatDisplayRows(
    segs: TableExtendedRiskRegister[],
    previous: RiskRegisterDisplayRow[],
): RiskRegisterDisplayRow[] {
    const known = new Map(previous.map((row) => [row.key, row]));
    return segs.map((seg, index) => segRow(known, seg, FLAT_GROUP_KEY, seg.numberOfWorkers, seg.numberOfContractors, index % 2 === 0));
}

export function buildGroupedDisplayRows(
    segs: TableExtendedRiskRegister[],
    rows: RiskRegisterGroupedRowResponse[],
    groups: RiskRegisterGroupResponse[],
    fallbackLabel: string,
    previous: RiskRegisterDisplayRow[],
    collapsedGroupKeys: ReadonlySet<string> = new Set(),
): RiskRegisterDisplayRow[] {
    const known = new Map(previous.map((row) => [row.key, row]));
    const collapsible = isCollapsible(groups);
    const segsById = new Map(segs.map((seg) => [seg.segId, seg]));
    const labels = buildGroupLabels(groups, fallbackLabel);
    const result: RiskRegisterDisplayRow[] = [];
    let lastGroupKey: string | null = null;
    let segIndex = 0;
    rows.forEach((row) => {
        const seg = segsById.get(row.segId);
        if (!seg) {
            return;
        }
        const groupKey = groupKeyOf(row.orgLevelId);
        const collapsed = isCollapsed(collapsible, collapsedGroupKeys, groupKey);
        if (groupKey !== lastGroupKey) {
            lastGroupKey = groupKey;
            const shown = row.orgLevelName ?? fallbackLabel;
            const label = labels.get(groupKey) ?? { name: shown, label: groupLabelOf(shown, 0), title: shown, segCount: 0 };
            const header: RiskRegisterGroupHeaderRow = {
                kind: RiskRegisterDisplayRowKind.GroupHeader,
                key: `header|${groupKey}`,
                groupKey,
                ...label,
                collapsible,
                collapsed,
            };
            result.push(
                reuseOrCreate(
                    known,
                    header,
                    (old) =>
                        old.label === header.label &&
                        old.title === header.title &&
                        old.segCount === header.segCount &&
                        old.collapsible === collapsible &&
                        old.collapsed === collapsed,
                ),
            );
        }
        if (collapsed) {
            return;
        }
        result.push(segRow(known, seg, groupKey, row.numberOfWorkers, row.numberOfContractors, segIndex % 2 === 0));
        segIndex++;
    });
    return result;
}

function isCollapsible(groups: RiskRegisterGroupResponse[]): boolean {
    return groups.length > 1;
}

function isCollapsed(collapsible: boolean, collapsedGroupKeys: ReadonlySet<string>, groupKey: string): boolean {
    return collapsible && collapsedGroupKeys.has(groupKey);
}

export function hasOpenRowsAhead(
    groups: RiskRegisterGroupResponse[],
    loadedRowCount: number,
    collapsedGroupKeys: ReadonlySet<string>,
): boolean {
    const collapsible = isCollapsible(groups);
    let end = 0;
    return groups.some((group) => {
        end += group.segCount;
        return end > loadedRowCount && !isCollapsed(collapsible, collapsedGroupKeys, groupKeyOf(group.orgLevelId));
    });
}

export function mergeGroupedRows(
    rows: RiskRegisterGroupedRowResponse[],
    incoming: RiskRegisterGroupedRowResponse[],
): RiskRegisterGroupedRowResponse[] {
    const known = new Set(rows.map(pairKey));
    return [...rows, ...incoming.filter((row) => !known.has(pairKey(row)))];
}

function pairKey(row: RiskRegisterGroupedRowResponse): string {
    return `${groupKeyOf(row.orgLevelId)}|${row.segId}`;
}

export function hasSamePairs(
    current: RiskRegisterGroupedRowResponse[],
    refreshed: RiskRegisterGroupedRowResponse[],
    segIds: ReadonlySet<string>,
): boolean {
    const currentKeys = new Set(current.map(pairKey));
    const refreshedKeys = new Set(refreshed.filter((row) => segIds.has(row.segId)).map(pairKey));
    const loadedGroupKeys = new Set(current.map((row) => groupKeyOf(row.orgLevelId)));
    const stillPresent = current.filter((row) => segIds.has(row.segId)).every((row) => refreshedKeys.has(pairKey(row)));
    const noneAdded = refreshed
        .filter((row) => segIds.has(row.segId) && loadedGroupKeys.has(groupKeyOf(row.orgLevelId)))
        .every((row) => currentKeys.has(pairKey(row)));
    return stillPresent && noneAdded;
}

export function hasSameGroups(
    rowCount: number,
    groups: RiskRegisterGroupResponse[],
    refreshedRowCount: number,
    refreshedGroups: RiskRegisterGroupResponse[],
): boolean {
    return (
        rowCount === refreshedRowCount &&
        groups.length === refreshedGroups.length &&
        groups.every(
            (group, index) =>
                groupKeyOf(group.orgLevelId) === groupKeyOf(refreshedGroups[index].orgLevelId) &&
                group.segCount === refreshedGroups[index].segCount,
        )
    );
}

export function applyRefreshedRows(
    current: RiskRegisterGroupedRowResponse[],
    refreshed: RiskRegisterGroupedRowResponse[],
): RiskRegisterGroupedRowResponse[] {
    const byPair = new Map(refreshed.map((row) => [pairKey(row), row]));
    return current.map((row) => byPair.get(pairKey(row)) ?? row);
}

export function selectGroupedEntries<TSource>(
    rows: RiskRegisterDisplayRow[],
    sources: ReadonlyMap<string, TSource>,
): RiskRegisterGroupedEntry<TSource>[] {
    const groups: { header: RiskRegisterGroupHeaderRow; items: { row: RiskRegisterSegDisplayRow; source: TSource }[] }[] = [];
    rows.forEach((row) => {
        if (row.kind === RiskRegisterDisplayRowKind.GroupHeader) {
            groups.push({ header: row, items: [] });
            return;
        }
        const source = sources.get(row.seg.segId);
        if (source !== undefined) {
            groups[groups.length - 1]?.items.push({ row, source });
        }
    });
    return groups.flatMap(({ header, items }) => {
        const trimmed: RiskRegisterGroupHeaderRow = { ...header, segCount: items.length, label: groupLabelOf(header.name, items.length) };
        return items.map(({ row, source }, index) => ({ header: trimmed, row, source, startsGroup: index === 0 }));
    });
}
